// useDeviceIdleSignOut — keeping the promise the Devices card already makes.
// Spec: docs/specs/device-and-worker-sign-in.md §4.6, D6. Phase 3 remainder.
//
// ⚠️⚠️ THIS EXISTS BECAUSE THE APP WAS ALREADY TELLING PEOPLE IT DID THIS.
//
// `AppDevicesCard` renders "Signs out after 5 min idle" under every profile.
// The manuals repeat it — "Supervisor tablet … signs out after 5 minutes idle",
// "HR kiosk … signs out after 2 minutes". `max_idle_seconds` has been a column
// since mig 20260814123144, and NOTHING HAS EVER READ IT except that label.
//
// So a shared wall tablet, signed in as whoever last used it, stayed signed in
// indefinitely. On an HR kiosk — the one profile allowed to show pay — that is
// the next person in the queue reading somebody's payslip. The screen said it
// would not happen, which is worse than saying nothing: a manager who reads
// "signs out after 2 minutes" walks away.
//
// ⚠️ ONLY ON A PAIRED DEVICE. An office computer is not a shared tablet, and
// signing an ordinary user out mid-task because they read a long report would
// be a new bug in exchange for a fixed one. `isPairedDevice()` is the gate.
//
// ⚠️ POLLED, NOT SCHEDULED. A single setTimeout never fires on a tablet that
// slept through the window — which is precisely the case that matters, because
// a tablet nobody touched is a tablet that slept. Every decision here compares
// stored timestamps against the clock now, so waking up locks immediately.

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { isPairedDevice } from '@/lib/deviceSession'
import { getCachedProfile } from '@/lib/deviceSync'
import { signOutThisDevice } from '@/lib/signOutThisDevice'

/**
 * Used when a device is paired but its profile has not been fetched yet.
 *
 * ⚠️ NOT "no timeout". A paired device is by definition shared, so an unknown
 * window must resolve toward locking, not toward staying open. 900s is the
 * column default in mig 20260814123144, so this is the same answer the server
 * would give.
 */
export const PLATFORM_DEFAULT_IDLE_SECONDS = 900

/** How often the stamps are compared. */
const TICK_MS = 15_000

/** Events that count as somebody being here. */
const ACTIVITY: readonly string[] = ['pointerdown', 'keydown', 'wheel', 'touchstart']

/**
 * Has this session been idle past its device's window?
 *
 * Exported for the test — the branching is the part worth pinning, not the
 * listener plumbing.
 */
export function isIdleExpired(
  lastActivityMs: number,
  idleSeconds: number,
  nowMs: number,
): boolean {
  // A stamp in the future means the clock moved backwards. Treat it as idle
  // rather than as "no time has passed", which would hold a session open on a
  // tablet whose RTC reset — exactly the device this protects.
  if (lastActivityMs > nowMs) return true
  const windowMs = Math.max(0, idleSeconds || PLATFORM_DEFAULT_IDLE_SECONDS) * 1000
  return nowMs - lastActivityMs > windowMs
}

/**
 * The window to enforce, given what this device knows about itself.
 *
 * ⚠️ Returns null for "do not enforce", which happens only when the browser is
 * not a paired device at all. A paired device with no profile still gets a
 * window — see PLATFORM_DEFAULT_IDLE_SECONDS.
 */
export function idleWindowSeconds(
  paired: boolean,
  profileIdleSeconds: number | null | undefined,
): number | null {
  if (!paired) return null
  if (!profileIdleSeconds || profileIdleSeconds <= 0) return PLATFORM_DEFAULT_IDLE_SECONDS
  return profileIdleSeconds
}

/**
 * Sign the worker out of a shared device once it has been left alone.
 *
 * Mounted once in the authenticated shell, OUTSIDE every gate
 * (<SharedDeviceSignOuts/>): a gate that renders a prompt instead of its
 * children would otherwise unmount this and leave the device signed in under
 * the prompt indefinitely. Does nothing at all on a machine that is not a
 * paired app device.
 */
export function useDeviceIdleSignOut(): void {
  const navigate = useNavigate()
  const lastActivity = useRef(Date.now())
  const signingOut = useRef(false)

  useEffect(() => {
    const seconds = idleWindowSeconds(isPairedDevice(), getCachedProfile()?.max_idle_seconds)
    if (seconds === null) return

    const touch = () => { lastActivity.current = Date.now() }
    for (const e of ACTIVITY) window.addEventListener(e, touch, { passive: true })
    // Coming back to a backgrounded tab is not activity — but it IS the moment
    // to re-check, because the poll below does not run while hidden.
    const onVisible = () => { if (!document.hidden) void check() }
    document.addEventListener('visibilitychange', onVisible)

    async function check() {
      if (signingOut.current) return
      if (!isIdleExpired(lastActivity.current, seconds as number, Date.now())) return
      signingOut.current = true
      // The shared path (src/lib/signOutThisDevice.ts): local session first,
      // the server sign-out bounded, the stored session dropped if that could
      // not reach anyone, then the lock screen. The consent prompt's Sign out
      // button takes the same path, so the two cannot drift apart.
      await signOutThisDevice(navigate)
    }

    const id = window.setInterval(() => { void check() }, TICK_MS)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      for (const e of ACTIVITY) window.removeEventListener(e, touch)
    }
  }, [navigate])
}
